import * as React from "react";
import { styled } from "@mui/material/styles";
import { Form, FormSelectChoice } from "../interfaces";
import {
  Grid,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
} from "@mui/material";
import { useEffect, useState } from "react";

const PREFIX = "MultiSelectCheckbox";

const classes = {
  eventTypes: `${PREFIX}-eventTypes`,
  checkbox: `${PREFIX}-checkbox`,
};

const StyledGrid = styled(Grid)({
  [`& .${classes.eventTypes}`]: {
    "marginLeft": ".5rem",
    "paddingBottom": 4,
    "& >span.MuiTypography-body1": {
      lineHeight: "1rem",
    },
  },
  [`& .${classes.checkbox}`]: {
    "paddingTop": 4,
    "paddingBottom": 4,

    "&&:hover": {
      backgroundColor: "transparent",
    },
  },
});

export interface Props {
  title: string;
  choices: FormSelectChoice[];
  fieldName: string;
  setForm: (form: any) => void;
}

export default function MultiSelectCheckbox({
  title = "",
  choices = [],
  fieldName,
  setForm,
}: Props) {
  const selectAllValue = `${title}_Select All`;
  const id = title.toLowerCase().replace(/\s/g, "-");
  const selectAllChoice = {
    label: "Select All",
    value: selectAllValue,
    checked: choices.every((choice: FormSelectChoice) => choice.checked),
  };
  const [allChoices, setAllChoices] = useState<FormSelectChoice[]>([
    selectAllChoice,
    ...choices,
  ]);

  const choiceChanged = (event: any) => {
    if (event.target.value !== selectAllValue) {
      // create the new choices to be updated with the selections
      setAllChoices((prevChoices: FormSelectChoice[]) => {
        let newChoices = prevChoices.map((choice: FormSelectChoice) => ({
          ...choice,
          checked:
            choice.value.toString() === event.target.value
              ? event.target.checked
              : choice.checked,
        }));

        //remove "select all" from new choices
        const newChoicesValues = newChoices.slice(1);

        // counts how many choices are currently unchecked
        const unSelectedCount = newChoicesValues.filter(
          (choice: FormSelectChoice) => !choice.checked,
        ).length;

        // the first choice is always "select all"
        // "select all" should be checked only if all choices are selected
        newChoices[0].checked = unSelectedCount === 0;

        return newChoices;
      });
    } else {
      setAllChoices((prevState: FormSelectChoice[]) =>
        prevState.map((choice: FormSelectChoice) => ({
          ...choice,
          checked: event.target.checked || false,
        })),
      );
    }
  };

  // when the choices are changed outside the input, we want to update it here as well to reflect the desired values
  useEffect(() => {
    setAllChoices([selectAllChoice, ...choices]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choices]);

  useEffect(() => {
    const updatedValues = allChoices
      .filter(
        (choice: FormSelectChoice) =>
          choice.value !== selectAllValue && choice.checked,
      )
      .map((choice: FormSelectChoice) => choice.value);
    // the flat array function does work outside the browser so this is a workaround to flatten the array
    setForm((prevForm: Form) => ({
      ...prevForm,
      [fieldName]: [].concat.apply([], updatedValues as never[]) || [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChoices]);

  return (
    <StyledGrid container justifyContent="space-around" id={`${id}-checkbox`}>
      <Grid>
        <FormControl component="fieldset">
          <FormLabel
            component="legend"
            style={{
              height: 1,
              margin: -1,
              display: "block",
              overflow: "hidden",
              padding: 0,
              position: "absolute",
              width: 1,
            }}
          >
            {title}
          </FormLabel>
          <FormGroup>
            {allChoices.map((choice: any) => {
              // ID prop to allow searching for input selection
              const id = choice.label
                .replace(/[^a-zA-Z ]/g, "")
                .replace(/\s\s+/g, " ")
                .replace(/\s/g, "-")
                .toLowerCase();
              const labelId = `${id}-label`;

              return (
                <FormControlLabel
                  className={classes.eventTypes}
                  key={labelId}
                  id={labelId}
                  control={
                    <Checkbox
                      className={classes.checkbox}
                      onClick={choiceChanged}
                      color="primary"
                      checked={choice.checked}
                      inputProps={{
                        "aria-labelledby": labelId,
                        "id": `${id}-${choice.checked}`,
                      }}
                      value={choice.value}
                      id={id}
                    />
                  }
                  label={choice.label}
                />
              );
            })}
          </FormGroup>
        </FormControl>
      </Grid>
    </StyledGrid>
  );
}
